import {
    useEffect, useMemo, useState,
} from 'react'
import omit from 'lodash/omit'
import isEmpty from 'lodash/isEmpty'

import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    RequestConfig,
} from 'app/types/request.types'
import {
    AddressCategory,
    ServiceCenterStatus,
    ServiceCenterType, StorageType,
} from 'app/enums/label.enums'
import request from 'app/requests/ServiceCenter/ServiceCenter.requests'
import {
    useGetLocation,
} from 'app/Apps/ContactManagement/Locations/hooks'

import {
    ServiceCenterFormDefaultType,
    ServiceCenterFormType,
} from '../serviceCenters.types'
import getFields from './serviceCenterForm.fields'
import useStyles from './serviceCenterForm.style'

const EMPTY_ARRAY = []

const ServiceCenterForm = ({
    data,
    onSuccess = null,
    disabled = false,
    exists = false,
    customButtons = EMPTY_ARRAY,
    requestParam,
}: Readonly<{
    data?: ServiceCenterFormType | ServiceCenterFormDefaultType,
    onSuccess?: (serviceCenter?: ServiceCenterFormType) => void,
    disabled?: boolean,
    exists?: boolean,
    customButtons?: JSX.Element[],
    requestParam?: RequestConfig,
}>) => {
    const getAllowedValues = useEnumValues()

    const [
        value,
        setValue,
    ] = useState<Partial<ServiceCenterFormType>>(data ? {
        ...data,
    } : {})

    const {
        classes,
    } = useStyles({
        isProductionSite: value?.serviceCenterType === ServiceCenterType.PRODUCTION_SITE,
        exists,
    })

    const isPlannedStatus = value?.serviceCenterStatus === ServiceCenterStatus.PLANNED
    const isProductionSite = value?.serviceCenterType === ServiceCenterType.PRODUCTION_SITE

    const {
        data: locationData,
    } = useGetLocation(value?.location?.id)

    const hasClientConsignmentStock = useMemo(() => {
        return locationData?.addressCategory?.includes(AddressCategory.CLIENT_CONSIGNMENT_STOCK)
    }, [locationData])

    const fields = getFields({
        getAllowedValues,
        isProductionSite,
        isPlannedStatus,
        isLunchBreak: value?.isLunchBreak,
        hasClientConsignmentStock,
        exists,
        disabled,
    })

    const fieldsWithClasses = useFieldsWithClassName(fields, classes)

    useEffect(() => {
        if (!value?.isLunchBreak && (value?.breakStartsAt || value?.breakEndsAt)) {
            setValue({
                ...value,
                breakStartsAt: undefined,
                breakEndsAt: undefined,
            })
        }
    }, [value])

    useEffect(() => {
        if (hasClientConsignmentStock) {
            setValue((prevValue) => {
                return {
                    ...prevValue,
                    serviceCenterType: ServiceCenterType.CLIENT_CONSIGNMENT_STOCK,
                    isAvailableForDropoff: false,
                    isAvailableForPickup: false,
                }
            })
        }
    }, [hasClientConsignmentStock])

    const modifyDataBeforeSend = (
        dataForm: ServiceCenterFormType,
    ): Partial<ServiceCenterFormType> => {
        let requestData = dataForm

        if (!isPlannedStatus) {
            requestData = omit(requestData, [
                'serviceCenterPlannedStartDate',
                'isLunchBreak',
            ])
        }
        if (!isProductionSite) {
            requestData = omit(requestData, [
                'containerProductionRate',
                'isLunchBreak',
            ])
        }

        return {
            ...requestData,
            productMetrics: requestData?.productMetrics?.filter((item) => {
                return !isEmpty(item) && Object.values(item).every((el) => {
                    return Boolean(el)
                })
            }),
            containerProductionRate: requestData?.containerProductionRate?.filter((item) => {
                return !isEmpty(item) && Object.values(item).every((el) => {
                    return Boolean(el) || el === 0
                })
            }),
            storages: requestData?.storages?.filter((storage) => {
                return !isEmpty(storage)
                    && (
                        (storage.storageType === StorageType.DRY
                            && Boolean(storage.storageCapacity)) || storage.storageCapacity === 0
                        || Object.values(storage).every((el) => {
                            return Boolean(el) || el === 0
                        }))
            }),
        }
    }

    return (
        <FormWithControls
            onSuccess={onSuccess}
            value={value}
            setExternalValue={setValue}
            name={request.domainName}
            fields={fieldsWithClasses}
            disabled={disabled}
            requestParams={requestParam}
            modifyDataBeforeSend={modifyDataBeforeSend}
            classNames={{
                gridWrapper: classes.gridWrapper,
            }}
            customButtons={customButtons}
            exists={exists}
        />
    )
}

export default ServiceCenterForm
